import { useEffect } from 'react';

interface SEOMetaTagsProps {
  title: string;
  description: string;
  keywords?: string;
  author?: string;
  imageUrl?: string;
  url?: string;
  type?: 'website' | 'article';
  publishedTime?: string;
  modifiedTime?: string;
  category?: string;
}

const SEOMetaTags: React.FC<SEOMetaTagsProps> = ({
  title,
  description,
  keywords,
  author = 'Akademik Kadro',
  imageUrl,
  url,
  type = 'article',
  publishedTime,
  modifiedTime,
  category
}) => {
  // Create meta description (max 160 characters)
  const metaDescription = description.length > 160 
    ? `${description.substring(0, 157)}...` 
    : description;
  
  // Full page title with site name
  const fullTitle = `${title} - Akademik Kadro`;
  
  useEffect(() => {
    // Update document title
    document.title = fullTitle;
    
    // Helper function to update or create meta tags
    const setMetaTag = (name: string, content: string, property = false) => {
      const attributeName = property ? 'property' : 'name';
      let element = document.querySelector(`meta[${attributeName}="${name}"]`) as HTMLMetaElement;
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attributeName, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };
    
    // Basic Meta Tags
    setMetaTag('description', metaDescription);
    if (keywords) setMetaTag('keywords', keywords);
    if (author) setMetaTag('author', author);
    
    // Open Graph / Facebook
    setMetaTag('og:type', type, true);
    if (url) setMetaTag('og:url', url, true);
    setMetaTag('og:title', title, true);
    setMetaTag('og:description', metaDescription, true);
    setMetaTag('og:site_name', 'Akademik Kadro', true);
    if (imageUrl) {
      setMetaTag('og:image', imageUrl, true);
      setMetaTag('og:image:type', 'image/jpeg', true);
      setMetaTag('og:image:width', '1200', true);
      setMetaTag('og:image:height', '630', true);
      setMetaTag('og:image:alt', title, true);
    }
    
    // Twitter
    setMetaTag('twitter:card', 'summary_large_image', true);
    setMetaTag('twitter:site', '@akademikadro', true);
    setMetaTag('twitter:creator', '@akademikadro', true);
    if (url) setMetaTag('twitter:url', url, true);
    setMetaTag('twitter:title', title, true);
    setMetaTag('twitter:description', metaDescription, true);
    if (imageUrl) {
      setMetaTag('twitter:image', imageUrl, true);
      setMetaTag('twitter:image:alt', title, true);
    }
    
    // Article Specific
    if (type === 'article') {
      if (publishedTime) setMetaTag('article:published_time', publishedTime, true);
      if (modifiedTime) setMetaTag('article:modified_time', modifiedTime, true);
      if (category) setMetaTag('article:section', category, true);
      if (author) setMetaTag('article:author', author, true);
    }
    
    // Update canonical URL
    if (url) {
      let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute('href', url);
    }
    
    // Add JSON-LD for articles
    if (type === 'article' && url && publishedTime) {
      let scriptElement = document.querySelector('script[type="application/ld+json"]');
      
      if (!scriptElement) {
        scriptElement = document.createElement('script');
        scriptElement.setAttribute('type', 'application/ld+json');
        document.head.appendChild(scriptElement);
      }
      
      const jsonLdData = {
        '@context': 'https://schema.org',
        '@type': 'NewsArticle',
        'headline': title,
        'description': metaDescription,
        'image': imageUrl,
        'author': {
          '@type': 'Person',
          'name': author
        },
        'publisher': {
          '@type': 'Organization',
          'name': 'Akademik Kadro',
          'logo': {
            '@type': 'ImageObject',
                            'url': 'https://akademikadro.com/Akademikadro_Logo.jpeg'
          }
        },
        'datePublished': publishedTime,
        'dateModified': modifiedTime || publishedTime,
        'mainEntityOfPage': {
          '@type': 'WebPage',
          '@id': url
        }
      };
      
      scriptElement.textContent = JSON.stringify(jsonLdData);
    }
    
    // Clean up function
    return () => {
      // No need to remove meta tags as they'll be updated on next render
    };
  }, [title, description, keywords, author, imageUrl, url, type, publishedTime, modifiedTime, category, fullTitle, metaDescription]);
  
  // This component doesn't render anything visible
  return null;
};

export default SEOMetaTags; 